<script>
import { mapActions, mapGetters } from 'vuex'
import CandidatePreferenceModal from '@/components/candidate/candidate-preference-modal.vue'
import { CandidatePreferenceUpdateMode } from '@/shared/constants/candidate/CandidatePreferenceUpdateMode'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'

const CandidatePreferredState = Object.freeze({
  None: 'none',
  Some: 'some',
  All: 'all',
})

const ClientPreferredLevel = 'CLIENT'

export default {
  name: 'CandidatePreferredButton',
  components: { CandidatePreferenceModal },
  props: {
    candidate: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      VuetifyColourClass,
      showPreferenceModal: false,
      isPreferenceUpdating: false,
      preferenceClientIds: [],
    }
  },
  computed: {
    ...mapGetters({
      selectedClientIds: 'client/getSelectedClients',
      allClients: 'auth/getAllClients',
      getClientLocations: 'auth/getClientLocations',
    }),
    fullName() {
      return `${this.candidate.firstName} ${this.candidate.lastName}`.trim()
    },
    clientPreferredCount() {
      if (typeof this.candidate.preferredLocationCount === 'number') {
        return this.candidate.preferredLocationCount
      }

      const clientLevel = (this.candidate.preferredLevels ?? []).find(
        ({ preferredLevelSetAt }) =>
          preferredLevelSetAt === ClientPreferredLevel
      )

      return clientLevel?.linkCount ?? 0
    },
    preferredState() {
      const accessibleLocationCount =
        this.candidate.accessibleLocationCount ?? this.selectedClientIds.length

      if (accessibleLocationCount === 0 || this.clientPreferredCount === 0) {
        return CandidatePreferredState.None
      }

      if (this.clientPreferredCount === accessibleLocationCount) {
        return CandidatePreferredState.All
      }

      return CandidatePreferredState.Some
    },
    hasPreferredLocations() {
      return this.preferredState !== CandidatePreferredState.None
    },
    preferredIcon() {
      const iconMap = {
        [CandidatePreferredState.None]: 'mdi-star-outline',
        [CandidatePreferredState.Some]: 'mdi-star-half-full',
        [CandidatePreferredState.All]: 'mdi-star',
      }

      return iconMap[this.preferredState]
    },
    starIconColor() {
      return this.preferredState !== CandidatePreferredState.None
        ? VuetifyColourClass.warning400
        : VuetifyColourClass.grey400
    },
  },
  watch: {
    selectedClientIds(newIds, oldIds) {
      if (JSON.stringify(newIds) !== JSON.stringify(oldIds)) {
        this.showPreferenceModal = false
      }
    },
  },
  methods: {
    ...mapActions({
      setCandidatePreferred: 'candidate/setCandidatePreferred',
    }),
    resolveDirectToggleLocationId() {
      if (
        this.selectedClientIds.length !== 1 ||
        this.candidate?.accessibleLocationCount !== 1 ||
        typeof this.candidate.preferredLocationCount !== 'number'
      ) {
        return null
      }

      const [clientId] = this.selectedClientIds
      const clientLocations = this.getClientLocations(clientId) ?? []

      return clientLocations.length === 1 ? clientLocations[0].locationId : null
    },
    async openPreferred() {
      const directToggleLocationId = this.resolveDirectToggleLocationId()

      if (directToggleLocationId) {
        await this.toggleSingleLocationPreference(directToggleLocationId)
        return
      }

      this.preferenceClientIds = [...this.selectedClientIds]
      this.showPreferenceModal = true
    },
    async toggleSingleLocationPreference(locationId) {
      if (this.isPreferenceUpdating) return

      const [clientId] = this.selectedClientIds
      const isCurrentlyPreferred = this.candidate.preferredLocationCount === 1
      const mode = isCurrentlyPreferred
        ? CandidatePreferenceUpdateMode.None
        : CandidatePreferenceUpdateMode.Locations

      this.isPreferenceUpdating = true

      const result = await this.setCandidatePreferred({
        candidateId: this.candidate.candidateId,
        payload: {
          clientIds: [clientId],
          changes: [
            {
              clientId,
              mode,
              preferredLocationIds:
                mode === CandidatePreferenceUpdateMode.Locations
                  ? [locationId]
                  : [],
            },
          ],
        },
      })

      if (result.error) {
        this.$toast.error(
          this.$tc(
            isCurrentlyPreferred
              ? 'candidateList.preference.removed.error'
              : 'candidateList.preference.added.error',
            1
          )
        )
      } else {
        this.$emit('preference-updated')
      }

      this.isPreferenceUpdating = false
    },
    handlePreferredSaved() {
      this.$emit('preference-updated')
    },
  },
}
</script>

<template>
  <span
    class="candidate-preferred-button preferred-star ml-2 flex-shrink-0"
    :class="{
      'preferred-star--active': hasPreferredLocations,
    }"
  >
    <v-btn
      icon
      x-small
      class="pa-1"
      :loading="isPreferenceUpdating"
      :aria-label="
        $t('candidateList.card.managePreferredAriaLabel', {
          name: fullName,
          state: $t(`candidateList.card.preferredState.${preferredState}`),
        })
      "
      :aria-busy="isPreferenceUpdating ? 'true' : 'false'"
      :disabled="
        selectedClientIds.length === 0 || isPreferenceUpdating || disabled
      "
      @click.stop="openPreferred"
      @keydown.stop
    >
      <v-icon :color="starIconColor" size="22">{{ preferredIcon }}</v-icon>
    </v-btn>

    <CandidatePreferenceModal
      v-if="showPreferenceModal"
      v-model="showPreferenceModal"
      :candidate-id="candidate.candidateId"
      :candidate-name="fullName"
      :preferred-location-count="clientPreferredCount"
      :candidate-accessible-location-count="candidate.accessibleLocationCount"
      :client-ids="preferenceClientIds"
      :available-client-count="allClients.length"
      @saved="handlePreferredSaved"
    />
  </span>
</template>

<style lang="scss">
@import '@/styles/_colors.scss';
@import '@/styles/_r2b-colours.scss';
@import '@/styles/_vue-transitions.scss';

.preferred-star .v-btn.v-btn--icon {
  width: 32px;
  height: 32px;
  border-radius: 8px;
}

.preferred-star .v-btn.v-btn--icon:not(.v-btn--disabled) {
  &:hover,
  &:focus-visible {
    background-color: $brand-blue-25;

    .v-icon {
      color: $brand-blue-600 !important;
    }
  }

  .v-icon {
    transition: color 0.2s ease;
  }
}

.preferred-star .v-btn__content {
  width: 22px;
  height: 22px;
}

.preferred-star--active .v-btn.v-btn--icon:not(.v-btn--disabled) {
  .v-icon {
    color: $color-warning-400 !important;
  }

  &:hover,
  &:focus-visible {
    background-color: $semantics-warning-100;

    .v-icon {
      color: $color-warning-400 !important;
    }
  }
}
</style>
